import { ReconciliationModel } from '../models';

/**
 * Utility functions for working with reconciliation data from daily consolidated reports
 */

// Type definitions
export interface DailySummary {
  reconciliationModel?: ReconciliationModel;
  reconciliation?: any; // Raw reconciliation data
  report_date?: string;
  date?: string;
  [key: string]: any; // Allow additional properties
}

export interface ReconciliationStatus {
  type: 'none' | 'positive' | 'negative' | 'zero';
  label: string;
  color: 'default' | 'success' | 'error' | 'info';
}

export interface ReconciliationSummary {
  totalCount: number;
  positiveCount: number;
  negativeCount: number;
  zeroCount: number;
  totalPositiveAmount: number;
  totalNegativeAmount: number;
  netAmount: number;
}

/**
 * Extract reconciliation data from a daily summary object
 * @param dailySummary - Daily summary object that may contain reconciliation data
 * @returns ReconciliationModel instance or null if no reconciliation
 */
export const getReconciliationFromSummary = (dailySummary: DailySummary | null | undefined): ReconciliationModel | null => {
  if (!dailySummary) return null;
  
  // Check if reconciliation data is already processed into a model
  if (dailySummary.reconciliationModel && dailySummary.reconciliationModel instanceof ReconciliationModel) {
    return dailySummary.reconciliationModel;
  }
  
  // Check if raw reconciliation data exists
  if (dailySummary.reconciliation) {
    return ReconciliationModel.fromReconciliationNode(dailySummary.reconciliation);
  }
  
  return null;
};

/**
 * Extract reconciliation data from daily summaries array
 * @param dailySummaries - Array of daily summary objects
 * @param date - Optional date to filter by (YYYY-MM-DD format)
 * @returns ReconciliationModel instance or null if no reconciliation found
 */
export const getReconciliationFromSummaries = (
  dailySummaries: DailySummary[], 
  date: string | null = null
): ReconciliationModel | null => {
  if (!Array.isArray(dailySummaries) || dailySummaries.length === 0) {
    return null;
  }
  
  // If date is specified, find the summary for that date
  if (date) {
    const summaryForDate = dailySummaries.find(summary => 
      summary.report_date === date || summary.date === date
    );
    return getReconciliationFromSummary(summaryForDate);
  }
  
  // Return the first reconciliation found (most recent)
  for (const summary of dailySummaries) {
    const reconciliation = getReconciliationFromSummary(summary);
    if (reconciliation) {
      return reconciliation;
    }
  }
  
  return null;
};

/**
 * Check if a daily summary has reconciliation data
 * @param dailySummary - Daily summary object
 * @returns True if reconciliation data exists
 */
export const hasReconciliation = (dailySummary: DailySummary): boolean => {
  return getReconciliationFromSummary(dailySummary) !== null;
};

/**
 * Get all reconciliations from daily summaries
 * @param dailySummaries - Array of daily summary objects
 * @returns Array of ReconciliationModel instances
 */
export const getAllReconciliations = (dailySummaries: DailySummary[]): ReconciliationModel[] => {
  if (!Array.isArray(dailySummaries)) return [];
  
  return dailySummaries
    .map(summary => getReconciliationFromSummary(summary))
    .filter((reconciliation): reconciliation is ReconciliationModel => reconciliation !== null);
};

/**
 * Format reconciliation amount for display
 * @param amount - Amount value from reconciliation
 * @returns Formatted currency string
 */
export const formatReconciliationAmount = (amount: string | number | null | undefined): string => {
  if (!amount) return '$0.00';
  
  const numAmount = parseFloat(amount.toString());
  if (isNaN(numAmount)) return '$0.00';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(numAmount);
};

/**
 * Get reconciliation status for display
 * @param reconciliation - Reconciliation model instance
 * @returns Status object with type, label, and color
 */
export const getReconciliationStatus = (reconciliation: number | undefined): ReconciliationStatus => {
  if (!reconciliation) {
    return { type: 'none', label: 'No Reconciliation', color: 'default' };
  }
  
  if (reconciliation > 0) {
    return { type: 'positive', label: 'Over', color: 'success' };
  } else if (reconciliation < 0) {
    return { type: 'negative', label: 'Short', color: 'error' };
  } else {
    return { type: 'zero', label: 'Balanced', color: 'info' };
  }
};

/**
 * Check if reconciliation data needs attention (non-zero amount)
 * @param reconciliation - Reconciliation model instance
 * @returns True if reconciliation amount is non-zero
 */
export const needsAttention = (reconciliation: ReconciliationModel | null): boolean => {
  return reconciliation !== null && !reconciliation.isZero;
};

/**
 * Get reconciliation summary for a store
 * @param dailySummaries - Array of daily summary objects
 * @returns Summary object with counts and total amounts
 */
export const getReconciliationSummary = (dailySummaries: DailySummary[]): ReconciliationSummary => {
  const reconciliations = getAllReconciliations(dailySummaries);
  
  const summary: ReconciliationSummary = {
    totalCount: reconciliations.length,
    positiveCount: 0,
    negativeCount: 0,
    zeroCount: 0,
    totalPositiveAmount: 0,
    totalNegativeAmount: 0,
    netAmount: 0
  };
  
  reconciliations.forEach(reconciliation => {
    if (reconciliation.isPositive) {
      summary.positiveCount++;
      summary.totalPositiveAmount += reconciliation.amountValue;
    } else if (reconciliation.isNegative) {
      summary.negativeCount++;
      summary.totalNegativeAmount += Math.abs(reconciliation.amountValue);
    } else {
      summary.zeroCount++;
    }
    
    summary.netAmount += reconciliation.amountValue;
  });
  
  return summary;
}; 